import axios from "axios";
import React, { useEffect, useState } from "react";
import { captureConfiguredDoor } from '../../../lib/captureConfiguredDoor';
import { Input } from "../ui/Input";
import { useAppContext } from "../../../context/AppContext";
import { Button, buttonVariants } from "../ui/Button";
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "../ui/Form";

import useLocalStorage from "../../../lib/UseLocalStorage";
import QuotePriceSummary from "./QuotePriceSummary";
import PriceAccessNote from "./PriceAccessNote";
import { basketEcommerceParams, trackEvent } from "../../../lib/analytics";

const TradeForm = ({ methods, doorSvgRef }) => {
    const [showBasketButton, setShowBasketButton] = useState(false);
    const [basket, setBasket] = useState(null);
    const [showAccountSuccess, setShowAccountSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [accountLoading, setAccountLoading] = useState(false);
    const [installerAutofilled, setInstallerAutofilled] = useState(false);
    const { csrfToken, setCurrentPage, state, setView } = useAppContext();
    const { setValue } = useLocalStorage();
    const [error, setError] = useState("")
    const { setError: setFormError, clearErrors, setFocus } = methods;
    const key = window.location.pathname.split('/')[1]

    const {
        control,
        formState: { errors },
    } = methods;

    useEffect(() => {
        if (installerAutofilled) {
            return;
        }

        const installerProfile = window.initialData?.installer_profile;
        if (!installerProfile || typeof installerProfile !== "object") {
            setInstallerAutofilled(true);
            return;
        }

        const nameRaw = String(installerProfile.primary_contact ?? "").trim();
        const nameParts = nameRaw.split(/\s+/).filter(Boolean);
        const firstNameFromInstaller = nameParts[0] ?? "";
        const lastNameFromInstaller =
            nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

        const nextDefaults = {
            first_name: firstNameFromInstaller,
            last_name: lastNameFromInstaller,
            email: String(installerProfile.email ?? "").trim(),
            phone: String(installerProfile.phone ?? "").trim(),
            postcode: String(installerProfile.postcode ?? "").trim(),
        };

        const dirty = methods.formState?.dirtyFields ?? {};
        const shouldAutofill = (fieldName) => {
            if (dirty?.[fieldName]) {
                return false;
            }
            const current = methods.getValues(fieldName);
            return String(current ?? "").trim() === "";
        };

        Object.entries(nextDefaults).forEach(([fieldName, value]) => {
            if (!String(value ?? "").trim()) {
                return;
            }
            if (!shouldAutofill(fieldName)) {
                return;
            }
            methods.setValue(fieldName, value, { shouldValidate: true });
        });

        setInstallerAutofilled(true);
    }, [installerAutofilled, methods]);

    const applyServerErrors = (serverErrors) => {
        if (!serverErrors || typeof serverErrors !== "object") {
            return false;
        }

        clearErrors();

        Object.entries(serverErrors).forEach(([field, messages]) => {
            const message = Array.isArray(messages) ? messages[0] : String(messages);
            setFormError(field, { type: "server", message });
        });

        const firstField = Object.keys(serverErrors)[0];
        if (firstField) {
            setFocus(firstField);
        }

        return true;
    };

    const onSubmit = async (e) => {

        try {
            setError("")
            clearErrors();
            setLoading(true);
            const previousView = state.view;
            const waitForPaint = () =>
                new Promise((resolve) =>
                    requestAnimationFrame(() => requestAnimationFrame(resolve))
                );

            // Get the fixed viewBox from the SVG to ensure consistent framing
            const fixedViewBox = doorSvgRef.current?.getAttribute("viewBox");

            setView("external");
            await waitForPaint();
            const doorImageExternal = await captureConfiguredDoor(doorSvgRef, fixedViewBox);

            setView("internal");
            await waitForPaint();
            const doorImageInternal = await captureConfiguredDoor(doorSvgRef, fixedViewBox);

            setView(previousView);
            await waitForPaint();
            const res = await axios.post("/basket", {
                ...e,
                door_image_external: doorImageExternal,
                door_image_internal: doorImageInternal,
                _token: csrfToken,
                key,
                type: "trade"
            });

            if (res?.data?.basket?.id) {
                setLoading(false);
                setBasket(res.data.basket);
                setShowBasketButton(true);
                const ecommerce = basketEcommerceParams(res.data.basket, "trade");
                trackEvent("generate_lead", ecommerce);
                trackEvent("add_to_cart", ecommerce);
                setValue("basketId", res?.data?.basket?.id);
                return;
            }

            if (res?.data?.status === "error") {
                const applied = applyServerErrors(res?.data?.errors);
                setError((!applied && res?.data?.message) ? res.data.message : "");
                setLoading(false);
                return;
            }

            setError("Something went wrong.");
            setLoading(false);
        } catch (error) {
            setLoading(false);
            const serverErrors = error?.response?.data?.errors;
            const applied = applyServerErrors(serverErrors);
            const message = error?.response?.data?.message
                || (error?.response
                    ? "Something went wrong. Please try again."
                    : "We couldn't reach the server. Please check your connection and try again.");
            setError(applied ? "" : message);
        }
    };

    return (
        <>
            {showBasketButton ? (
                <div className="flex flex-col items-center justify-center gap-6 h-full w-full p-4">
                    <h2 className="uppercase text-2xl text-center text-gray-700">
                        Successfully <br /> added to basket
                    </h2>
                    <QuotePriceSummary basket={basket} />
                    <button
                        onClick={() => {
                            trackEvent("begin_checkout", basketEcommerceParams(basket, "trade"));
                            setCurrentPage("basket");
                        }}
                        type="button"
                        className={buttonVariants({
                            className:
                                "flex items-center justify-between text-center gap-1 w-fit uppercase",
                            })}
                    >
                        go to checkout
                    </button>
                </div>
            ) : (
                <form
                    onSubmit={methods.handleSubmit(onSubmit)}
                    className="flex flex-col items-start justify-between w-full h-full min-h-0 px-4 py-4"
                >
                    <div className="flex flex-col gap-4 items-center w-full [&>div]:w-full flex-1 min-h-0 overflow-y-auto pb-4 px-2 mx-2">
                        <PriceAccessNote />
                        <FormField
                            control={control}
                            name="first_name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-gray-700">
                                        First Name
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="text"
                                            icon
                                            className="text-gray-700"
                                            placeholder="Enter Your  First Name"
                                            autoComplete="given-name"
                                            {...field}
                                        />
                                    </FormControl>
                                    {errors.first_name && (
                                        <FormMessage>
                                            {errors.first_name.message}
                                        </FormMessage>
                                    )}
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={control}
                            name="last_name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-gray-700">
                                        Last Name
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="text"
                                            icon
                                            className="text-gray-700"
                                            placeholder="Enter Your Last Name"
                                            autoComplete="family-name"
                                            {...field}
                                        />
                                    </FormControl>
                                    {errors.last_name && (
                                        <FormMessage>
                                            {errors.last_name.message}
                                        </FormMessage>
                                    )}
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-gray-700">
                                        Email
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="text"
                                            icon
                                            className="text-gray-700"
                                            placeholder="Enter Your Email"
                                            autoComplete="email"
                                            inputMode="email"
                                            {...field}
                                        />
                                    </FormControl>
                                    {errors.email && (
                                        <FormMessage>
                                            {errors.email.message}
                                        </FormMessage>
                                    )}
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={control}
                            name="postcode"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-gray-700">
                                        Postcode
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="text"
                                            icon
                                            className="text-gray-700"
                                            placeholder="Enter Your Postcode"
                                            autoComplete="postal-code"
                                            {...field}
                                        />
                                    </FormControl>
                                    {errors.postcode && (
                                        <FormMessage>
                                            {errors.postcode.message}
                                        </FormMessage>
                                    )}
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={control}
                            name="phone"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-gray-700">
                                        Phone Number
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="text"
                                            icon
                                            className="text-gray-700"
                                            placeholder="Enter Your  Phone Number"
                                            autoComplete="tel"
                                            inputMode="tel"
                                            {...field}
                                        />
                                    </FormControl>
                                    {errors.phone && (
                                        <FormMessage>
                                            {errors.phone.message}
                                        </FormMessage>
                                    )}
                                </FormItem>
                            )}
                        />
                    </div>
                    {error && (
                        <div role="alert" className='text-red-500 text-sm pb-3'>{error}</div>
                    )}
                    <Button type="submit" disabled={loading} aria-busy={loading} className="w-full uppercase mb-[var(--mobile-bottom-clearance)] lg:mb-0">
                        {loading ? "Getting your price…" : "Get quote"}
                    </Button>
                </form>
            )}
        </>
    );
};

export default TradeForm;
